import { inject, Injectable } from '@angular/core';
import {
    FormArray,
    FormControl,
    FormGroup,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import {
    nameOf,
    tryParseToNumber,
} from '../../../../../../core/functions/string-helpers';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { ActionCode } from '../../../../../../core/models/resources/action-code';
import { ApplicationFormControlManagerService } from '../../../../shared/services/application-form-control-manager/application-form-control-manager.service';
import { ApplicationStateService } from '../../../../shared/services/application-state/application-state.service';
import { SchedulesDAndHSharedFormService } from '../../../shared/services/schedules-d-and-h-shared-form/schedules-d-and-h-shared-form.service';
import { Antenna } from '../../models/antenna';
import { AntennaFormModel } from '../../models/antenna-form-model';
import { SchedulesDAndHFormService } from '../schedules-d-and-h-form/schedules-d-and-h-form.service';

@Injectable({
    providedIn: 'root',
})
export class AntennaFormService {
    private readonly controlsOnlyForFixedLocations: (keyof AntennaFormModel)[] =
        [
            'isOmniDirectional',
            'heightAboveAverageTerrain',
            'heightToTip',
            'azimuth',
            'beamwidth',
            'polarizationCode',
            'gain',
        ];

    private readonly controlsOnlyForNonOmniDirectional: (keyof AntennaFormModel)[] =
        ['azimuth', 'beamwidth', 'polarizationCode', 'gain'];

    private readonly schedulesDAndHFormService = inject(
        SchedulesDAndHFormService,
    );
    private readonly applicationStateService = inject(ApplicationStateService);
    private readonly schedulesDAndHSharedFormService = inject(
        SchedulesDAndHSharedFormService,
    );
    private readonly applicationFormControlManager = inject(
        ApplicationFormControlManagerService,
    );

    constructor() {}

    getFormDefinition(): FormGroup<FormGroupType<AntennaFormModel>> {
        return new FormGroup<FormGroupType<AntennaFormModel>>({
            id: new FormControl<number | null>(null),
            number: new FormControl<number | null>(null),
            locationNumber: new FormControl<number | null>(null),
            actionCode: new FormControl<ActionCode | null>(ActionCode.Add),
            isDeleted: new FormControl<boolean | null>(null),
            tempId: new FormControl<string | null>(null),
            isOmniDirectional: new FormControl<boolean | null>(null),
            heightAboveAverageTerrain: new FormControl<string | null>(null),
            heightToTip: new FormControl<string | null>(null),
            azimuth: new FormControl<string | null>(null),
            beamwidth: new FormControl<string | null>(null),
            polarizationCode: new FormControl<string | null>(null),
            gain: new FormControl<string | null>(null),
        });
    }

    /**
     * Toggles the validators for the antenna form.
     *
     * If the application is an existing license application and the antenna has not been added or
     * modified, then we should not apply the validators as the data from ULS should be considered valid (unless the user modifies the data).
     *
     * Otherwise, the validators should be applied to ensure that the data is valid if the user has added a new antenna or modified an existing antenna.
     *
     * @param antenna the antenna form group to toggle the validators on
     */
    toggleValidators(
        antenna: FormGroup<FormGroupType<AntennaFormModel>>,
    ): void {
        const isExistingLicenseApplication =
            this.applicationStateService.IsExistingLicenseApplication();
        const isAddedOrModified =
            antenna.dirty ||
            antenna.controls.actionCode.value === ActionCode.Add ||
            antenna.controls.actionCode.value === ActionCode.Modify;

        // If the application is an existing license application and the antenna has not been added or
        // modified, then we should not apply the validators as the data from the ULS should be
        // considered valid.
        if (isExistingLicenseApplication && !isAddedOrModified) {
            return;
        }

        const antennaValidatorMap = this.getFormControlValidationMap();
        const antennaValidatorMapKeys = Object.keys(
            antennaValidatorMap,
        ) as Array<keyof AntennaFormModel>;

        for (const controlName of antennaValidatorMapKeys) {
            const control = antenna.controls[controlName];
            const validators = antennaValidatorMap[controlName];

            if (control && validators) {
                control.setValidators(validators);
                control.updateValueAndValidity({ emitEvent: false });
            }
        }
    }

    /**
     * Toggles the form controls based on the location fixed value.
     * @param isLocationFixed true if the location for the antenna is fixed
     * @param antenna the antenna form group to update
     */
    toggleFormControlsForLocationChange(
        antenna: FormGroup<FormGroupType<AntennaFormModel>>,
        isLocationFixed: boolean,
    ): void {
        this.controlsOnlyForFixedLocations.forEach((controlName) => {
            const control = antenna.controls[controlName];

            if (isLocationFixed) {
                this.applicationFormControlManager.enable(
                    control,
                    { emitEvent: false },
                    () => {
                        control.markAsTouched();
                    },
                );
            } else {
                this.applicationFormControlManager.disable(
                    control,
                    { emitEvent: false },
                    () => {
                        control.reset();
                    },
                );
            }
        });
    }

    /**
     * Toggles the form controls based on the omni directional value.
     * @param omni the current omni value
     * @param antenna the antenna form group to update
     */
    toggleFormControlsForOmniDirectionalChange(
        antenna: FormGroup<FormGroupType<AntennaFormModel>>,
    ): void {
        const disableNonOmniControls =
            antenna.controls.isOmniDirectional.value ?? true;

        this.controlsOnlyForNonOmniDirectional.forEach((controlName) => {
            const control = antenna.controls[controlName];

            if (disableNonOmniControls) {
                this.applicationFormControlManager.disable(
                    control,
                    { emitEvent: false },
                    () => {
                        control.reset();
                    },
                );
            } else {
                this.applicationFormControlManager.enable(
                    control,
                    { emitEvent: false },
                    () => {
                        control.markAsTouched();
                    },
                );
            }
        });
    }

    /**
     * Refreshes the antenna numbers based on the location number.
     * @param antennasControl
     */
    refreshAntennaNumbers(antennasControl: FormArray): void {
        // Have to use `getRawValue` since some of the controls are disabled
        const antennas =
            antennasControl.getRawValue() as Partial<AntennaFormModel>[];

        // Group antennas by location number
        const groupedAntennas: {
            [locationNumber: number]: Partial<AntennaFormModel>[];
        } = {};

        antennas.forEach((antenna) => {
            if (!groupedAntennas[antenna.locationNumber!]) {
                groupedAntennas[antenna.locationNumber!] = [];
            }

            groupedAntennas[antenna.locationNumber!].push(antenna);
        });

        // Increment the number of each antenna in each location
        Object.keys(groupedAntennas).forEach((locationNumber) => {
            const locationAntennas = groupedAntennas[parseInt(locationNumber)];
            if (!locationAntennas) {
                return;
            }

            locationAntennas.forEach((antenna, index) => {
                antenna.number = index + 1;
            });
        });

        antennasControl.patchValue(antennas, { emitEvent: false });
    }

    /**
     * Patches the form value with the response. This is necessary because the API response types are different from the form types.
     * The form values are strings, but the API response values are numbers because it gets serialized as numbers.
     */
    patchFormValue(
        form: FormArray<FormGroup<FormGroupType<AntennaFormModel>>>,
        response: Antenna[],
    ): void {
        form.patchValue(this.getFormModelValue(response));
    }

    /**
     * Gets the value of the form array. Some values need to be set to null if they are empty strings.
     * @param form
     * @returns
     */
    getValue(
        form: FormArray<FormGroup<FormGroupType<AntennaFormModel>>>,
    ): Antenna[] {
        return form.getRawValue().map((antenna) => {
            return {
                ...antenna,
                heightAboveAverageTerrain: tryParseToNumber(
                    antenna.heightAboveAverageTerrain,
                ),
                heightToTip: tryParseToNumber(antenna.heightToTip),
                azimuth: tryParseToNumber(antenna.azimuth),
                beamwidth: tryParseToNumber(antenna.beamwidth),
                gain: tryParseToNumber(antenna.gain),
            };
        });
    }

    getFormModelValue(response: Antenna[]): AntennaFormModel[] {
        return response.map((antenna) => {
            return {
                ...antenna,
                isDeleted:
                    antenna.actionCode === ActionCode.Delete ? true : null,
                tempId: null,
                heightAboveAverageTerrain:
                    antenna.heightAboveAverageTerrain?.toString() ?? null,
                heightToTip: antenna.heightToTip?.toString() ?? null,
                azimuth: antenna.azimuth?.toString() ?? null,
                beamwidth: antenna.beamwidth?.toString() ?? null,
                gain: antenna.gain?.toString() ?? null,
            };
        });
    }

    markActionCodeOnAntennaChange(
        control: FormControl,
        antennas: FormArray<FormGroup<FormGroupType<AntennaFormModel>>>,
        antennasResponse: Antenna[],
    ): boolean {
        return this.schedulesDAndHSharedFormService.markActionCodeOnChange(
            control,
            antennas,
            this.getFormModelValue(antennasResponse),
            [
                ...this.schedulesDAndHSharedFormService.defaultOmitFields,
                nameOf<AntennaFormModel>('isOmniDirectional'),
            ],
        );
    }

    /**
     * Map of the form controls and their corresponding validators. Form controls, by default, will
     * not contain any validators. Validators get applied in specific scenarios and this map will
     * be used to apply the correct validators to the correct form controls in those scenarios.
     */
    private getFormControlValidationMap(): Partial<
        Record<keyof AntennaFormModel, ValidatorFn[]>
    > {
        return {
            locationNumber: [Validators.required],
            isOmniDirectional: [Validators.required],
            heightAboveAverageTerrain: [Validators.required],
            heightToTip: [
                Validators.required,
                this.schedulesDAndHFormService.antennaHeightToTipValidator(),
            ],
            azimuth: [Validators.min(0), Validators.max(360)],
        };
    }
}
